import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StarInput = ({ value, onChange }) => (
  <div className="flex gap-1.5" data-testid="review-star-input">
    {[1, 2, 3, 4, 5].map((n) => (
      <button key={n} type="button" data-testid={`star-${n}`} onClick={() => onChange(n)}
        aria-label={`${n} star${n > 1 ? "s" : ""}`} className="p-0.5">
        <Star size={30} strokeWidth={0}
          className={n <= value ? "fill-[#C9A227] text-[#C9A227]" : "fill-[#E8DCC4] text-[#E8DCC4]"} />
      </button>
    ))}
  </div>
);

export default function ReviewPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const t = searchParams.get("t") || "";
  const [data, setData] = useState(null);
  const [bad, setBad] = useState(false);
  const [sel, setSel] = useState(null);
  const [form, setForm] = useState({ rating: 0, title: "", text: "", name: "" });
  const [photo, setPhoto] = useState(null);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState({});

  useEffect(() => {
    axios.get(`${API}/review/${encodeURIComponent(orderId)}`, { params: { t } })
      .then((r) => {
        setData(r.data);
        const firstOpen = r.data.items.find((i) => !i.reviewed);
        setSel(firstOpen ? firstOpen.sku : null);
        setForm((f) => ({ ...f, name: r.data.first_name || "" }));
      })
      .catch(() => setBad(true));
  }, [orderId, t]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) { toast.error("Please pick a star rating."); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/review/${encodeURIComponent(orderId)}`,
        { ...form, sku: sel }, { params: { t } });
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        await axios.post(`${API}/review/${encodeURIComponent(orderId)}/photo/${r.data.review_id}`, fd,
          { params: { t } }).catch(() => toast.error("Review saved; the photo didn't upload."));
      }
      setDone((d) => ({ ...d, [sel]: true }));
      setForm((f) => ({ ...f, rating: 0, title: "", text: "" }));
      setPhoto(null);
      toast.success("Thank you — your review is in for moderation.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit — please try again.");
    } finally { setSending(false); }
  };

  const inputCls = "w-full bg-white border border-[#E8DCC4] rounded-sm px-4 py-2.5 text-base text-neutral-900 focus:outline-none focus:border-[#C9A227] focus:ring-1 focus:ring-[#C9A227]";

  return (
    <div className="min-h-screen bg-[#F5F3EC]" data-testid="review-page">
      <Header />
      <div className="max-w-xl mx-auto px-4 py-10">
        {bad && (
          <div className="bg-white border border-[#E8DCC4] p-8 text-center" data-testid="review-invalid">
            <h1 className="font-display text-2xl mb-2">This review link isn't valid</h1>
            <p className="text-sm text-neutral-500">Links from your order emails are signed and can't be edited. If you believe this is an error, reply to your order email.</p>
          </div>
        )}
        {data && !bad && (
          <div className="bg-white border border-[#E8DCC4] p-6 sm:p-8">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#A8871B] mb-2">Order {data.ref}</p>
            <h1 className="font-display text-2xl sm:text-3xl text-neutral-900 mb-2">
              How do you love it{data.first_name ? `, ${data.first_name}` : ""}?
            </h1>
            <p className="text-sm text-neutral-500 mb-6">
              Rate the piece you received — a short, honest note helps the next customer.
              Your review appears after our team reads it.
            </p>

            {data.items.length > 1 && (
              <div className="flex flex-wrap gap-2 mb-6" data-testid="review-item-picker">
                {data.items.map((i) => (
                  <button key={i.sku || i.name} onClick={() => setSel(i.sku)}
                    disabled={done[i.sku] || i.reviewed}
                    className={`flex items-center gap-2 border px-3 py-2 text-xs transition-colors ${
                      sel === i.sku ? "border-[#C9A227] bg-[#FDFBF6]" : "border-[#E8DCC4] hover:border-[#C9A227]"}
                      ${(done[i.sku] || i.reviewed) ? "opacity-40" : ""}`}
                    data-testid={`review-item-${i.sku}`}>
                    {i.image && <img src={i.image} alt={i.name} className="w-8 h-8 object-cover" />}
                    <span className="max-w-[160px] truncate">{i.name}</span>
                    {(done[i.sku] || i.reviewed) && <span className="text-[#A8871B]">✓</span>}
                  </button>
                ))}
              </div>
            )}

            {data.items.every((i) => i.reviewed || done[i.sku]) ? (
              <div className="text-center py-6" data-testid="review-all-done">
                <div className="flex justify-center gap-1 mb-3">
                  {[1,2,3,4,5].map((n) => <Star key={n} size={22} strokeWidth={0} className="fill-[#C9A227] text-[#C9A227]" />)}
                </div>
                <p className="font-display text-xl text-neutral-900 mb-1">Thank you!</p>
                <p className="text-sm text-neutral-500">Your review is in for moderation and will appear on the product page once our team reads it.</p>
              </div>
            ) : sel && !(done[sel]) && (
              <form onSubmit={submit} className="space-y-4" data-testid="review-form">
                <div>
                  <label className="block text-xs uppercase tracking-[0.12em] text-neutral-400 mb-1.5">Your rating</label>
                  <StarInput value={form.rating} onChange={(v) => setForm((f) => ({ ...f, rating: v }))} />
                </div>
                <input className={inputCls} placeholder="Title (optional)" value={form.title}
                  data-testid="review-title" maxLength={120}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
                <textarea className={inputCls} rows={4} required minLength={10} maxLength={2000}
                  placeholder="What do you love about it? Fit, sparkle, service…"
                  value={form.text} data-testid="review-text"
                  onChange={(e) => setForm((f) => ({ ...f, text: e.target.value }))} />
                <input className={inputCls} required maxLength={80} placeholder="Your first name"
                  value={form.name} data-testid="review-name"
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                <div>
                  <label className="block text-xs uppercase tracking-[0.12em] text-neutral-400 mb-1.5">
                    Photo (optional — JPG/PNG/WebP, under 5MB)
                  </label>
                  <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="review-photo"
                    className="text-sm text-neutral-600 file:mr-3 file:border file:border-[#E8DCC4] file:bg-white file:px-4 file:py-2 file:text-xs file:uppercase file:tracking-[0.12em] hover:file:border-[#C9A227]"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                </div>
                <button type="submit" disabled={sending} data-testid="review-submit"
                  className="w-full bg-[#C9A227] text-[#0D0D0D] text-sm font-bold tracking-[0.1em] uppercase py-3.5 hover:bg-[#b8932a] transition-colors disabled:opacity-50">
                  {sending ? "Sending…" : "Submit review"}
                </button>
              </form>
            )}
            {sel && done[sel] && (
              <p className="text-sm text-[#A8871B] mb-4" data-testid="review-thanks">Review received — thank you!</p>
            )}

            {data.google_review_url && (
              <div className="mt-8 pt-6 border-t border-[#EFE7D5] text-center">
                <p className="text-xs text-neutral-400 mb-3">Happy with the whole experience?</p>
                <a href={data.google_review_url} target="_blank" rel="noopener noreferrer"
                  data-testid="review-google-btn"
                  className="inline-block border border-[#C9A227] text-[#A8871B] text-xs font-bold tracking-[0.1em] uppercase px-6 py-3 hover:bg-[#FDFBF6] transition-colors">
                  Review us on Google
                </a>
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
